import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { VIOLATIONS_TAB_DETAILS_INITIAL_FILTERS } from '../../../shared/constants';
import {
  type ActiveFiltersState,
  type AdvancedFilterState,
} from '../../../shared/types';
import * as apiActions from '../data-layer/detailsApi';
import {
  type DetailsState,
  DetailsType,
  type TrackedViolationFunctions,
  type ViolationMitigatingControls,
} from '../data-layer/detailsTypes';

const initialState: DetailsState = {
  source: null,
  queryParam: undefined,
  sidepanel: { isSidePanelVisible: false },
  violationsTab: {
    isFooterVisible: true,
    conflicts: {
      selectedRowData: null,
      controls: {
        filters: VIOLATIONS_TAB_DETAILS_INITIAL_FILTERS,
        activeFilters: [] as ActiveFiltersState[],
        expandKey: null,
        searchKey: null,
      },
    },
    mitigate: { selectedRowData: null },
  },
};

const detailsSlice = createSlice({
  name: DetailsType.SLICE_NAME,
  initialState,
  reducers: {
    setQueryParam: (state, action: PayloadAction<string | undefined>) => {
      state.queryParam = action.payload;

      if (action.payload === undefined) {
        state.violationsTab.conflicts = {
          ...state.violationsTab.conflicts,
          selectedRowData: null,
          controls: {
            ...state.violationsTab.conflicts.controls,
            searchKey: null,
            expandKey: null,
          },
        };
      }
    },
    setDetailsSouce: (state, action: PayloadAction<string | null>) => {
      state.source = action.payload;
    },
    // Sidepanel actions
    setIsSidePanelVisible: (state, action: PayloadAction<boolean>) => {
      state.sidepanel.isSidePanelVisible = action.payload;
    },
    setSidePanelClose: (state) => {
      state.sidepanel = { isSidePanelVisible: false };
      state.violationsTab.conflicts = {
        ...state.violationsTab.conflicts,
        selectedRowData: null,
      };
      state.violationsTab.mitigate = {
        selectedRowData: null,
      };
    },
    // Violations Tab
    setViolationsTabConflictRow: (
      state,
      action: PayloadAction<TrackedViolationFunctions | null>
    ) => {
      state.violationsTab.conflicts.selectedRowData = action.payload;
    },
    // Violations Tab: Controls
    setViolationsTabFilters: (
      state,
      action: PayloadAction<AdvancedFilterState[]>
    ) => {
      state.violationsTab.conflicts.controls.filters = action.payload;
    },
    setViolationsTabActiveFilters: (
      state,
      action: PayloadAction<ActiveFiltersState[]>
    ) => {
      state.violationsTab.conflicts.controls.activeFilters = action.payload;
    },
    setViolationsTabSearckKey: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.violationsTab.conflicts.controls.searchKey = action.payload;
    },
    setViolationsTabExpandKey: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.violationsTab.conflicts.controls.expandKey = action.payload;
    },
    setSelectedMitigateRow: (
      state,
      action: PayloadAction<ViolationMitigatingControls | null>
    ) => {
      state.violationsTab.mitigate.selectedRowData = action.payload;
    },
    setViolationTabFooterStatus: (state, action: PayloadAction<boolean>) => {
      state.violationsTab.isFooterVisible = action.payload;
    },
    // Other sources
  },
});

const { reducer: detailsReducer, actions: sliceActions } = detailsSlice;

export const detailsActions = {
  ...sliceActions,
  ...apiActions,
};

export default detailsReducer;
